export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export type Attribution = Partial<Record<UtmKey, string>> & {
  landing_path?: string;
  captured_at?: string;
};

const STORAGE_KEY = "betup_attribution";
const MAX_STORED_TOUCHES = 20;

function hasAnyUtm(params: URLSearchParams): boolean {
  return UTM_KEYS.some((key) => {
    const v = params.get(key);
    return v != null && v.trim() !== "";
  });
}

function buildAttributionFromSearch(search: string): Attribution | null {
  const params = new URLSearchParams(search);
  if (!hasAnyUtm(params)) {
    return null;
  }

  const out: Attribution = {
    captured_at: new Date().toISOString(),
    landing_path:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : undefined,
  };

  for (const key of UTM_KEYS) {
    const raw = params.get(key);
    if (raw != null && raw.trim() !== "") {
      out[key] = raw.trim();
    }
  }

  return out;
}

/**
 * Multi-touch: appends UTM params from the current URL.
 * Dedupes identical UTM sets and caps stored touches.
 */
export function captureAttributionFromUrl(): void {
  if (typeof window === "undefined") return;

  try {
    const attribution = buildAttributionFromSearch(window.location.search);
    if (!attribution) {
      return;
    }
    const existing = getStoredAttribution() ?? [];
    const next = appendAttribution(existing, attribution);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private mode, quota)
  }
}

function stableUtmSignature(a: Attribution): string {
  const parts: string[] = [];
  for (const key of UTM_KEYS) {
    const v = a[key];
    if (typeof v === "string" && v.trim() !== "") {
      parts.push(`${key}=${v.trim()}`);
    }
  }
  return parts.join("&");
}

function appendAttribution(list: Attribution[], next: Attribution): Attribution[] {
  const sig = stableUtmSignature(next);
  if (sig === "") return list;

  const already = list.some((item) => stableUtmSignature(item) === sig);
  if (already) return list;

  const out = [...list, next];
  if (out.length <= MAX_STORED_TOUCHES) return out;
  return out.slice(out.length - MAX_STORED_TOUCHES);
}

export function getStoredAttribution(): Attribution[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed) return null;

    // Backward-compatible: previously stored a single object.
    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      return [parsed as Attribution];
    }

    if (!Array.isArray(parsed)) return null;
    return parsed.filter((x) => x && typeof x === "object") as Attribution[];
  } catch {
    return null;
  }
}

export function clearStoredAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

const MAX_UTM_VALUE_LEN = 512;
const MAX_LANDING_PATH_LEN = 2048;
const MAX_CAPTURED_AT_LEN = 64;

/**
 * Validates JSON from the register request body. Returns undefined if no valid UTM data.
 */
function sanitizeOneAttribution(input: unknown): Attribution | undefined {
  if (!input || typeof input !== "object") return undefined;
  const obj = input as Record<string, unknown>;
  const out: Attribution = {};

  for (const key of UTM_KEYS) {
    const v = obj[key];
    if (typeof v !== "string" || v.trim() === "") continue;
    out[key] = v.trim().slice(0, MAX_UTM_VALUE_LEN);
  }

  if (typeof obj.landing_path === "string" && obj.landing_path.trim() !== "") {
    out.landing_path = obj.landing_path.trim().slice(0, MAX_LANDING_PATH_LEN);
  }
  if (typeof obj.captured_at === "string" && obj.captured_at.trim() !== "") {
    out.captured_at = obj.captured_at.trim().slice(0, MAX_CAPTURED_AT_LEN);
  }

  const hasUtm = UTM_KEYS.some((k) => out[k]);
  if (!hasUtm) return undefined;
  return out;
}

export function sanitizeAttributionInput(input: unknown): Attribution[] | undefined {
  if (!input) return undefined;

  // Backward-compatible: single object.
  if (typeof input === "object" && !Array.isArray(input)) {
    const one = sanitizeOneAttribution(input);
    return one ? [one] : undefined;
  }

  if (!Array.isArray(input)) return undefined;
  const out: Attribution[] = [];
  for (const item of input) {
    const one = sanitizeOneAttribution(item);
    if (one) out.push(one);
  }
  if (out.length === 0) return undefined;
  return out.slice(0, MAX_STORED_TOUCHES);
}
